import { Box, Grid, IconButton, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { ExportButton } from '@commons/ExportButton';
import { getClientInfo } from '@selectors/client';
import { showErrorMessage } from '@actions/messageconfirmation';

import { generateExport } from './utils/export';
import { getMetricOptions } from './utils/config';
import { globalColumns } from './utils/column';
import GlobalStoreComparisonChart from './GlobalStoreComparisonChart';
import GlobalStoreTimeSeriesChart from './GlobalStoreTimeSeriesChart';
import MetricCard from '../common/MetricCard';

const VIEW_MODES = {
  TABLE: 'table',
  CHART: 'chart',
};

const GlobalPanel = ({
  selectedStores,
  enableTicketsNumber,
  currentPeriod,
  previousPeriod,
  showErrorMessage,
  globalData,
  globalTableData,
  isLoading,
  client: { storeName },
}) => {
  // Global states
  const metricOptions = getMetricOptions(enableTicketsNumber);
  const [globalViewMode, setGlobalViewMode] = useState(VIEW_MODES.TABLE);
  const [selectedStoreForChart, setSelectedStoreForChart] = useState(null);
  const [selectedMetricForChart, setSelectedMetricForChart] = useState(metricOptions[0]);

  // Global table columns with comparison

  const handleGridClick = (params) => {
    const store = selectedStores.find((s) => s.id === params.row.id);
    const metric = metricOptions.find((m) => m.id === params?.field);

    if (store) setSelectedStoreForChart(store);
    if (metric) {
      setSelectedMetricForChart(metric);
    } else {
      setSelectedMetricForChart(metricOptions.find((m) => m.id === 'turnoverExcludingTaxes'));
    }

    setGlobalViewMode(VIEW_MODES.CHART);
  };

  const handleCellClick = (params, event) => {
    // Prevent the event from bubbling up to trigger onRowClick
    event.stopPropagation();
    handleGridClick(params);
  };

  // Initialize selected store when stores are available
  useEffect(() => {
    if (selectedStores && selectedStores.length > 0 && !selectedStoreForChart) {
      setSelectedStoreForChart(selectedStores[0]);
    }
  }, [selectedStores]);

  // Export handler
  const handleExport = () => {
    try {
      generateExport(globalTableData, currentPeriod, previousPeriod, storeName);
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
    }
  };

  const lg_metric_card = enableTicketsNumber ? 2.4 : 4;

  return (
    <Box sx={{ mb: 5, paddingBottom: '64px' }}>
      {/* Metric Cards */}
      <Grid spacing={3} sx={{ mb: 5 }} container>
        {enableTicketsNumber && (
          <Grid lg={lg_metric_card} md={4} sm={6} xs={12} item>
            <MetricCard
              currentValue={globalData?.tickets?.current}
              isLoading={isLoading}
              previousValue={globalData?.tickets?.previous}
              title={i18next.t('ANALYSIS.PERFORMANCE.TICKETS')}
            />
          </Grid>
        )}
        <Grid lg={lg_metric_card} md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={globalData?.turnoverExcludingTaxes?.current}
            isLoading={isLoading}
            previousValue={globalData?.turnoverExcludingTaxes?.previous}
            title={i18next.t('GENERAL.TURNOVER_EX_TAX')}
            isCurrency
          />
        </Grid>
        {enableTicketsNumber && (
          <Grid lg={lg_metric_card} md={4} sm={6} xs={12} item>
            <MetricCard
              currentValue={globalData?.averageTicket?.current}
              isLoading={isLoading}
              previousValue={globalData?.averageTicket?.previous}
              title={i18next.t('ANALYSIS.PERFORMANCE.AVERAGE_TICKET')}
              hasDecimals
              isCurrency
            />
          </Grid>
        )}
        <Grid lg={lg_metric_card} md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={globalData?.realFoodConsumptionRatio?.current}
            isIncreasePositive={false}
            isLoading={isLoading}
            previousValue={globalData?.realFoodConsumptionRatio?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.REAL_FOOD_CONSUMPTION_RATIO')}
            isPercentage
          />
        </Grid>
        <Grid lg={lg_metric_card} md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={globalData?.storeCount?.current}
            isLoading={isLoading}
            previousValue={globalData?.storeCount?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.ACTIVE_STORE_COUNT', { storeName })}
          />
        </Grid>
      </Grid>

      {/* Table/Chart Toggle and Data */}
      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">
          {i18next.t('ANALYSIS.PERFORMANCE.GLOBAL_GRID_TITLE', { storeName })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: globalViewMode === 'table' ? 'white' : 'transparent',
                border: globalViewMode === 'table' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setGlobalViewMode(VIEW_MODES.TABLE)}
            >
              <img
                alt="Table"
                src="/images/inpulse/list-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: globalViewMode === 'chart' ? 'white' : 'transparent',
                border: globalViewMode === 'chart' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setGlobalViewMode(VIEW_MODES.CHART)}
            >
              <img
                alt="Chart"
                src="/images/inpulse/forecast-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Box>
          <ExportButton
            handleClick={handleExport}
            hasLabel={false}
            isDisabled={isLoading || !globalTableData.length}
          />
        </Box>
      </Box>

      {globalViewMode === VIEW_MODES.TABLE ? (
        <Box
          sx={{
            height: 600,
            maxHeight: 600,
            overflow: 'auto',
          }}
        >
          <DataGridPro
            columns={globalColumns(enableTicketsNumber)}
            initialState={{
              sorting: {
                sortModel: [{ field: 'storeName', sort: 'asc' }],
              },
            }}
            loading={isLoading}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            pageSize={20}
            rows={globalTableData}
            sx={{
              '& .MuiCircularProgress-root': {
                color: 'black',
              },
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            showToolbar
            onCellClick={handleCellClick}
            onRowClick={handleGridClick}
          />
        </Box>
      ) : (
        <GlobalStoreTimeSeriesChart
          currentPeriod={currentPeriod}
          enableTicketsNumber={enableTicketsNumber}
          initialSelectedMetric={selectedMetricForChart}
          initialSelectedStore={selectedStoreForChart}
          previousPeriod={previousPeriod}
          selectedStores={selectedStores}
        />
      )}

      {/* Store Comparison Chart */}
      <Box sx={{ mt: 4 }}>
        <GlobalStoreComparisonChart
          enableTicketsNumber={enableTicketsNumber}
          globalTableData={globalTableData}
          isLoading={isLoading}
          selectedStores={selectedStores}
        />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalPanel);
